import { Injectable, inject, signal } from '@angular/core';
import { dayjs } from '@karve.it/core';
import { Observable, raceWith, take, timer } from 'rxjs';

import { BrandingService } from '../../services/branding.service';

import { MenuItem } from 'primeng/api';
import { ProphetMetrics, ViewDatesQuery } from '../../../generated/graphql.generated';

export type ControlsGranularity = 'day' | 'week' | 'month';

/** The summary strip inputs a tab renders into the header row (the pieces
 *  moved out of the schedule card into the bar, LEFT of the filter button,
 *  so the calendar butts the panel's top edge — Ray). Carries exactly
 *  app-daily-summary's inputs; `onFilterByType` wires the strip's
 *  event-type badge click to the tab's own filter. */
export interface ControlsSummary {
    stats: NonNullable<ViewDatesQuery['viewDates']>[0] | null;
    totalRevenue: number;
    showForecast: boolean;
    showPacing: boolean;
    isDayView: boolean;
    forceShowRevenue: boolean;
    prophetMetrics: ProphetMetrics | null;
    allDays: NonNullable<ViewDatesQuery['viewDates']> | null;
    onFilterByType?(type: string): void;
}

/**
 * Per-tab registration for the schedule page's shared controls bar.
 * Exactly one tab's config is active at a time (the bar renders the
 * controls the active tab asks for).
 */
export interface ScheduleControlsConfig {
    /** day → single date picker + Today + prev/next; range → range picker + granularity presets. */
    mode: 'day' | 'range';
    /** Range mode only: which Day/Week/Month presets to show as chips. */
    granularities?: ControlsGranularity[];
    /** Day view only: show the pencil (edit-mode toggle). */
    canEdit?: boolean;
    /** Day view: toggles the gear menu (view options) from the shared bar. */
    gearMenu?: MenuItem[];
    /** Dispatch: the gear opens the tab's own overlay panel (needs the DOM event). */
    onGear?(event: Event): void;
    /** Pencil click (canEdit + onEdit required to show the pencil). */
    onEdit?(): void;
    /** Refresh button; absent = no refresh button. Returning an observable
     *  gates the bar's refresh button (spinner + disable) until it first
     *  emits — plus a hard 20s release valve so a dropped reload can never
     *  wedge the button disabled (Ray: no refresh toast). */
    onRefresh?(): void | Observable<unknown>;
    /** Filter button (e.g. the day view's event/asset types). Absent = no
     *  filter button. The tab owns the overlay; it gets the anchor event. */
    onFilters?(event: Event): void;
    /** Shown as a danger badge on the filter button. */
    filtersBadge?: () => number;
    /** Tab-specific action buttons rendered right of the spacer
     *  (e.g. dispatch's Assign Crew / Send schedule). */
    actions?: ScheduleControlsAction[];
    /** Navigation scale for the bar's prev/next arrows. Day views that
     *  display a week/month calendar grid set this per config (it follows
     *  the calendar's own view switch via updateMenu); absent = the range
     *  granularity or day. */
    navScale?: 'day' | 'week' | 'month';
    /** Range-mode preset window this tab's spans expand to (the schedules'
     *  week view = whole Mon–Sun weeks, FC firstDay 1). Overrides the shared
     *  persisted granularity for THIS tab's window math (step/Today/pick
     *  expansion) without polluting the other tabs' signal. */
    rangeGranularity?: ControlsGranularity;
}

/** A tab-specific button in the shared controls bar. Split-button menus
 *  (assign/schedule overlays) carry menuItems; plain buttons omit it. */
export interface ScheduleControlsAction {
    id: string;
    label: string;
    icon: string;
    /** Items for the split-button dropdown. A getter so the action always
     *  reflects the host tab's current menu (the tab rebuilds its models). */
    menuItems?: () => MenuItem[];
    /** Emphasized (filled) instead of outlined. */
    filled?: boolean;
    /** Disabled while the host tab's operation runs (no spinner support
     *  on this PrimeNG's p-splitButton). */
    loading?: boolean;
    /** Rendered as p-splitButton (with menu); default a plain button. */
    split?: boolean;
    /** Hidden on the tabview's mobile container width like the old header. */
    hideOnMobile?: boolean;
    onClick(): void;
}

/**
 * Single source of truth for the schedule page's date window. The bar is
 * just a view over these signals; each tab keeps itself synchronized via
 * effects (service date -> tab state) and writebacks (tab changes ->
 * service). The URL carries ISO `&date=` (and `&dateTo=` in range mode) so
 * deep links reopen on the same window.
 */
@Injectable({ providedIn: 'root' })
export class ScheduleControlsService {
    private activeConfig: ScheduleControlsConfig | null = null;

    private brandingService = inject(BrandingService);

    readonly config = signal<ScheduleControlsConfig | null>(null);
    readonly date = signal<Date | null>(null);
    readonly dateTo = signal<Date | null>(null);
    readonly granularity = signal<ControlsGranularity>('day');
    /** The active schedule tab's slug (day/week/month/dispatch/map/ops) —
     *  set by the schedule page on navigation; the date picker's variant
     *  (week/month/range/single) keys off it (crumb-date-picker). */
    readonly activeTabSlug = signal('');

    /** The active tab's summary strip (pacing/forecast/availability —
     *  rendered left of the filter button in the bar). The day/week/month
     *  tab sets it as its view data lands; every other tab clears it on
     *  registration (the strip is theirs to own). */
    readonly summary = signal<ControlsSummary | null>(null);

    /** True from click until the active tab's reload emits completion (or
     *  the 20s valve) — the bar spins + disables the refresh button. */
    readonly refreshing = signal(false);

    /** Wraps the bar's refresh click: claims the flag, arms the tab's
     *  completion observable. Re-entrant clicks while refreshing = no-op. */
    runRefresh(start: () => void | Observable<unknown>): void {
        if (this.refreshing()) { return; }
        this.refreshing.set(true);
        const release = () => this.refreshing.set(false);
        const done = start();
        if (done) {
            done.pipe(raceWith(timer(20000)), take(1)).subscribe({
                next: release,
                error: release,
                complete: release,
            });
        } else {
            // Tab gave no completion signal: release immediately (button
            // flashes as before instead of spinning blind).
            release();
        }
    }

    isRangeMode(): boolean {
        return this.config()?.mode === 'range';
    }

    /** The window-expansion granularity for the ACTIVE tab's range mode:
     *  the tab's own rangeGranularity when registered, else the shared
     *  persisted granularity (ops/map custom presets). */
    private effectiveGranularity(): ControlsGranularity {
        return this.config()?.rangeGranularity ?? this.granularity();
    }

    /**
     * Today as the BUSINESS's calendar day (CodeRabbit): the calendar runs
     * in the configured business timezone, so a browser in another timezone
     * near midnight must not select a different schedule day than it shows.
     */
    businessToday(): dayjs.Dayjs {
        const tz = this.brandingService.currentBranding?.value?.timezone;
        // The business CALENDAR day, returned as a BROWSER-LOCAL midnight:
        // every consumer reads these Dates by their local Y-M-D (writeUrl's
        // `dayjs(this.date()).format(...)`, the schedules' and readiness'
        // controls effects). `startOf('day')` on the business tz produced a
        // business MIDNIGHT instant — a browser west of the business
        // timezone read it as the previous calendar day and a repeated Today
        // landed on that day (CodeRabbit round 17). Formatting the business
        // date as a Y-M-D string and taking the LOCAL start of that day
        // keeps the two readings on the same calendar date.
        const day = tz ? dayjs().tz(tz).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        return dayjs(day).startOf('day');
    }

    setConfig(config: ScheduleControlsConfig): void {
        if (this.activeConfig === config) { return; }
        this.activeConfig = config;
        this.config.set(config);
        // The strip belongs to the REGISTERING tab: never leak the previous
        // tab's summary while the new tab's data loads.
        this.summary.set(null);
        // Registration is the first moment a tab's window mode is known. The
        // bar's bootstrap setters ran BEFORE this (root-scoped service) and
        // with no registered mode the writeUrl left dateTo unset — i.e. a
        // week deep link (?date=…, Mon–Sun seeded in the service) surfaced in
        // the URL as a bare ?date=… until a user action finally wrote the
        // end. Reconcile the retained window with the incoming tab's shape
        // FIRST (a foreign end must not survive the switch — Ray), then
        // reconcile the URL with the service window now that the mode is
        // known; a matching window is a no-op replaceState.
        this.reconcileRetainedWindow();
        this.writeUrl();
    }

    /**
     * A tab switch must not pair the new tab's anchor with an end inherited
     * from a DIFFERENT tab's flow (Ray: week→month→map reproduced a fake
     * Oct 1..Oct 11 window — the reused calendar's datesSet wrote the month
     * anchor while the old week config was still registered, so setDate's
     * day-clear did not fire, and the map's range-mode registration then
     * surfaced the retained week end as ?dateTo=…). After every registration
     * the window is either the new tab's own PRESET or an end deliberately
     * carried on the new tab's URL — never a mixed foreign pair. Runs before
     * setConfig's writeUrl so the URL reflects the reconciled window.
     */
    private reconcileRetainedWindow(): void {
        const config = this.config();
        if (!config) { return; }
        if (config.mode === 'day') {
            // A day view's window is one date: an end retained from a range
            // tab is foreign and would resurface on the next range tab's
            // registration (same class as setDate's clear — gated on a
            // REGISTERED config there so the bar's PRE-boot pair survives,
            // B15; here the mode is known, so the clear may run).
            if (this.dateTo()) { this.dateTo.set(null); }
            return;
        }
        if (config.rangeGranularity) {
            // Preset tab (the schedules' week view): the retained window must
            // BE the tab's preset for the current anchor; anything else (no
            // end yet, or an end shaped by another flow) is re-expanded —
            // same outcome the calendar's datesSet write-back enforces.
            const start = this.date();
            if (!start) { return; }
            const { start: presetStart, end: presetEnd } =
                this.presetWindowFor(dayjs(start), config.rangeGranularity);
            const end = this.dateTo();
            if (!end
                || !dayjs(start).isSame(presetStart, 'day')
                || !dayjs(end).isSame(presetEnd, 'day')) {
                this.applyPresetWindow();
            }
            return;
        }
        // Custom-range tab (map, ops-readiness): a retained end survives
        // only when the incoming tab's URL explicitly carries one (a deep
        // link, or the cross-tab carry) — an end merely held in the signal
        // from a previous tab's flow is stale and would otherwise be written
        // right back into the URL by this registration's writeUrl.
        if (this.dateTo() && !this.routeCarriesDateTo()) {
            this.dateTo.set(null);
        }
    }

    /** True when the CURRENT URL (the active tab's route query) explicitly
     *  carries a valid `dateTo` — an end attached to THIS tab's navigation
     *  (deep link / page-menu carry) rather than one retained in the signal
     *  from a previous tab's flow. */
    private routeCarriesDateTo(): boolean {
        try {
            const urlEnd = new URL(window.location.href).searchParams.get('dateTo');
            return !!urlEnd && dayjs(urlEnd).isValid();
        } catch {
            // URL sync best-effort (e.g. restricted contexts)
            return false;
        }
    }

    clearConfig(config: ScheduleControlsConfig): void {
        if (this.activeConfig === config) {
            this.activeConfig = null;
            this.config.set(null);
        }
    }

    setDate(date: Date): void {
        // Same-instant guard: two-way bindings replay values every change
        // detection; writing them back unconditionally would loop the
        // effects (bar <-> tab reload) forever.
        const current = this.date();
        if (current && date && current.getTime() === date.getTime()) {
            return;
        }
        this.date.set(date);
        // Range mode with a week/month preset and no range end yet (a deep
        // link like /schedule/week?date=...): expand the window so the bar
        // shows the preset range instead of a single day.
        if (this.isRangeMode() && this.granularity() !== 'day' && !this.dateTo()) {
            this.applyPresetWindow();
        } else if (this.config() && !this.isRangeMode() && this.dateTo()) {
            // A new day selection in a registered day view must not pair with
            // a range end left over from a range tab — the next range entry
            // would show a reversed/mismatched window (CodeRabbit). The clear
            // is gated on a REGISTERED config: before registration the window
            // belongs to the bar's bootstrap pair (date + dateTo are seeded in
            // one ngOnInit pass), and a pre-config setDate re-fire must not
            // drop the freshly seeded range end (B15 boot race).
            this.dateTo.set(null);
        }
        this.writeUrl();
    }

    setDateTo(dateTo: Date | null): void {
        const current = this.dateTo();
        if (current === dateTo || (current && dateTo && current.getTime() === dateTo.getTime())) {
            return;
        }
        this.dateTo.set(dateTo);
        this.writeUrl();
    }

    setGranularity(granularity: ControlsGranularity): void {
        this.granularity.set(granularity);
        this.applyPresetWindow();
        this.writeUrl();
    }

    goToday(): void {
        // Start-of-day + identity checks so a repeated Today click is a true
        // no-op (applyPresetWindow wrote fresh Date objects every click and
        // the readiness effect reloads on every emission — CodeRabbit).
        const today = this.businessToday();
        if (this.isRangeMode()) {
            // Effective scale follows the step()'s resolution: a tab with an
            // explicit navScale steps by that, presets use the tab's range
            // granularity or the shared persisted one.
            const g = this.config()?.navScale ?? this.effectiveGranularity();
            // Custom open-ended spans (the schedules' week view = next 7
            // days from the anchor, Ray; ops-readiness/map custom windows)
            // re-center their EXISTING span on today instead of collapsing
            // it to a single day.
            if (g === 'day') {
                const current = this.date();
                const currentTo = this.dateTo() ?? this.date();
                const spanDays = current && currentTo
                    ? Math.max(1, dayjs(currentTo).startOf('day').diff(dayjs(current).startOf('day'), 'day') + 1)
                    : 1;
                const end = today.add(spanDays - 1, 'day');
                if (current && currentTo
                    && current.getTime() === today.valueOf()
                    && dayjs(currentTo).startOf('day').valueOf() === end.startOf('day').valueOf()) {
                    return;
                }
                this.date.set(today.toDate());
                this.dateTo.set(end.toDate());
                this.writeUrl();
                return;
            }
            const { start, end } = this.presetWindowFor(today, g);
            const current = this.date();
            const currentTo = this.dateTo();
            if (current && currentTo
                && current.getTime() === start.valueOf()
                && currentTo.getTime() === end.valueOf()) {
                return;
            }
            this.date.set(start.toDate());
            this.dateTo.set(end.toDate());
            this.writeUrl();
            return;
        }
        this.setDate(today.toDate());
        this.applyPresetWindow();
        this.writeUrl();
    }

    step(dir: -1 | 1): void {
        // The displayed view's scale wins: the schedules calendar can show a
        // week or month grid (its own view switch), so the bar has to step
        // by that instead of the service's retained granularity (CodeRabbit).
        // Day-mode tabs that registered no navScale (e.g. the map tab after a
        // range selection) must step by DAY; the retained range granularity
        // would move them by a whole week/month (CodeRabbit).
        const rangeMode = this.config()?.mode === 'range';
        const g = this.config()?.navScale ?? (rangeMode ? this.effectiveGranularity() : 'day');
        const base = dayjs(this.date());
        // Custom range windows (no presets, e.g. ops readiness) step by
        // their own length so < > keeps the same window size; preset
        // granularities step by week/month as before.
        let spanDays = 1;
        if (rangeMode && g === 'day') {
            const end = dayjs(this.dateTo() ?? this.date());
            spanDays = Math.max(1, end.startOf('day').diff(base.startOf('day'), 'day') + 1);
            const nextStart = dir < 0 ? base.subtract(spanDays, 'day') : base.add(spanDays, 'day');
            this.date.set(nextStart.toDate());
            this.dateTo.set(nextStart.add(spanDays - 1, 'day').toDate());
            this.writeUrl();
            return;
        }
        let next: dayjs.Dayjs;
        if (g === 'week') {
            next = dir < 0 ? base.subtract(1, 'week') : base.add(1, 'week');
        } else if (g === 'month') {
            next = dir < 0 ? base.subtract(1, 'month') : base.add(1, 'month');
        } else {
            next = dir < 0 ? base.subtract(1, 'day') : base.add(1, 'day');
        }
        if (!rangeMode) {
            // A day-mode step must not leave a range end behind: going back to
            // a range tab cannot pair the moved start with the old dateTo
            // (CodeRabbit). setDate clears it.
            this.setDate(next.toDate());
            this.applyPresetWindow();
            this.writeUrl();
            return;
        }
        this.date.set(next.toDate());
        this.applyPresetWindow();
        this.writeUrl();
    }

    /** day → single day; week → Mon–Sun of the selected date's week;
     *  month → first–last day of the month. Shared with the schedule page's
     *  breadcrumb label so the bar, the label and the calendar's weekly view
     *  all agree on one week anchor (Monday). */
    presetWindowFor(current: dayjs.Dayjs, g: ControlsGranularity): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
        if (g === 'week') {
            const daysFromMonday = (current.day() + 6) % 7;
            const start = current.subtract(daysFromMonday, 'day').startOf('day');
            return { start, end: start.add(6, 'day').endOf('day') };
        }
        if (g === 'month') {
            return { start: current.startOf('month'), end: current.endOf('month') };
        }
        return { start: current, end: current };
    }

    private applyPresetWindow(): void {
        if (this.config()?.mode !== 'range') { return; }
        const g = this.effectiveGranularity();
        const { start, end } = this.presetWindowFor(dayjs(this.date()), g);
        this.date.set(start.toDate());
        this.dateTo.set(end.toDate());
    }

    private writeUrl(): void {
        try {
            const url = new URL(window.location.href);
            if (this.date()) {
                url.searchParams.set('date', dayjs(this.date()).format('YYYY-MM-DD'));
            }
            if (this.config()?.mode === 'range' && this.dateTo()) {
                url.searchParams.set('dateTo', dayjs(this.dateTo()).format('YYYY-MM-DD'));
            } else if (this.config()) {
                url.searchParams.delete('dateTo');
            }
            // No tab owns the config yet (the bar's bootstrap setters run
            // before registration): leave dateTo alone so a range deep link
            // survives the initialization writes (CodeRabbit).
            history.replaceState(null, '', url.toString());
        } catch {
            // URL sync best-effort (e.g. restricted contexts); the tab state lives on.
        }
    }
}
